import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api, shopToday } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import PhotoCapture from "@/components/PhotoCapture";

const PURITIES = ["", "24K", "22K", "21K", "18K", "14K", "9K", "SILVER", "OTHER"];
const METHODS = ["CASH", "CARD", "BANK TRANSFER", "PAYID", "AFTERPAY"];
const inputCls = "h-10 rounded-none";

const Field = ({ label, children, hint }) => (
  <div>
    <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      {label}
    </Label>
    <div className="mt-1.5">{children}</div>
    {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
  </div>
);

export default function RepairForm() {
  const { branch } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [form, setForm] = useState({
    customer_phone: "",
    customer_country_code: "+61",
    customer_name: "",
    customer_email: "",
    customer_address: "",
    item_description: "",
    karat: "",
    weight: "",
    work_required: "",
    quote_amount: "",
    deposit: "",
    deposit_method: "CASH",
    promised_date: "",
    notes: "",
  });

  // Pull the customer's details forward once the phone number looks complete.
  useEffect(() => {
    const phone = form.customer_phone.replace(/\D/g, "");
    if (phone.length < 8) return;
    let cancelled = false;
    api
      .get(`/customers/${encodeURIComponent(form.customer_phone)}`)
      .then(({ data }) => {
        const c = data.customer || data;
        if (cancelled || !c?.name) return;
        setForm((f) => ({
          ...f,
          customer_name: f.customer_name || c.name || "",
          customer_email: f.customer_email || c.email || "",
          customer_address: f.customer_address || c.address || "",
          customer_country_code: c.country_code || f.customer_country_code,
        }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [form.customer_phone]);

  const save = async () => {
    if (!form.customer_phone.trim()) return toast.error("Please enter the customer's phone number");
    if (!form.customer_name.trim()) return toast.error("Please enter the customer's name");
    if (!form.item_description.trim())
      return toast.error("Please describe the item being left with us");
    if (attachments.length === 0)
      return toast.error("Please take a photo of the item before booking it in");
    setSaving(true);
    try {
      const { data } = await api.post("/repairs", {
        branch,
        customer_phone: form.customer_phone.trim(),
        customer_country_code: form.customer_country_code || "+61",
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim(),
        customer_address: form.customer_address.trim(),
        item_description: form.item_description.trim(),
        karat: form.karat,
        weight: Number(form.weight) || 0,
        work_required: form.work_required.trim(),
        quote_amount: Number(form.quote_amount) || 0,
        deposit: Number(form.deposit) || 0,
        deposit_method: Number(form.deposit) > 0 ? form.deposit_method : "",
        promised_date: form.promised_date,
        notes: form.notes.trim(),
        attachments: attachments.map((a) => ({
          file_id: a.file_id,
          kind: a.kind || "before_photo",
          label: a.label || "",
          filename: a.filename || "",
          content_type: a.content_type || "",
          size: a.size || 0,
        })),
      });
      toast.success(`Repair R${data.repair_number} booked in`);
      navigate(`/repairs/${data.id}`);
    } catch (err) {
      toast.error(
        (err.response?.data?.detail && String(err.response.data.detail)) ||
          "Could not book that repair in"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fade-up max-w-4xl" data-testid="repair-form">
      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#B38F4E]">
        {branch}
      </p>
      <h1 className="mt-2 text-3xl font-bold sm:text-4xl">Book in a Repair</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Photograph the piece before it goes to the bench — it protects both the customer and the
        shop.
      </p>

      <section className="mt-6 border border-border p-5">
        <h2 className="text-base font-bold md:text-lg">Customer</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Phone *">
            <Input
              className={inputCls}
              value={form.customer_phone}
              onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
              data-testid="repair-phone-input"
            />
          </Field>
          <Field label="Name *">
            <Input
              className={inputCls}
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              data-testid="repair-name-input"
            />
          </Field>
          <Field label="Email" hint="Needed to send the ready-to-collect message">
            <Input
              className={inputCls}
              value={form.customer_email}
              onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
              data-testid="repair-email-input"
            />
          </Field>
          <Field label="Address">
            <AddressAutocomplete
              className={inputCls}
              value={form.customer_address}
              onChange={(v) => setForm({ ...form, customer_address: v })}
              placeholder="Start typing the address"
              testid="repair-address-input"
            />
          </Field>
        </div>
      </section>

      <section className="mt-5 border border-border p-5">
        <h2 className="text-base font-bold md:text-lg">The job</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field label="Item left with us *">
              <Input
                className={inputCls}
                placeholder="e.g. 22kt gold bangle, single stone missing"
                value={form.item_description}
                onChange={(e) => setForm({ ...form, item_description: e.target.value })}
                data-testid="repair-item-input"
              />
            </Field>
          </div>
          <Field label="Purity">
            <Select value={form.karat} onValueChange={(v) => setForm({ ...form, karat: v })}>
              <SelectTrigger className={inputCls} data-testid="repair-karat-select">
                <SelectValue placeholder="Not applicable" />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-white">
                {PURITIES.filter(Boolean).map((k) => (
                  <SelectItem key={k} value={k}>
                    {k}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Weight (gm)">
            <Input
              type="number"
              step="0.001"
              className={inputCls}
              value={form.weight}
              onChange={(e) => setForm({ ...form, weight: e.target.value })}
              data-testid="repair-weight-input"
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Work required">
              <Textarea
                className="min-h-[90px] rounded-none"
                value={form.work_required}
                onChange={(e) => setForm({ ...form, work_required: e.target.value })}
                data-testid="repair-work-input"
              />
            </Field>
          </div>
          <Field label="Quoted price (AUD)">
            <Input
              type="number"
              step="0.01"
              className={inputCls}
              value={form.quote_amount}
              onChange={(e) => setForm({ ...form, quote_amount: e.target.value })}
              data-testid="repair-quote-input"
            />
          </Field>
          <Field label="Promised by">
            <Input
              type="date"
              className={inputCls}
              min={shopToday()}
              value={form.promised_date}
              onChange={(e) => setForm({ ...form, promised_date: e.target.value })}
              data-testid="repair-promised-input"
            />
          </Field>
          <Field label="Deposit taken (AUD)">
            <Input
              type="number"
              step="0.01"
              className={inputCls}
              value={form.deposit}
              onChange={(e) => setForm({ ...form, deposit: e.target.value })}
              data-testid="repair-deposit-input"
            />
          </Field>
          <Field label="Deposit paid by">
            <Select
              value={form.deposit_method}
              onValueChange={(v) => setForm({ ...form, deposit_method: v })}
            >
              <SelectTrigger className={inputCls} data-testid="repair-deposit-method-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-white">
                {METHODS.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="sm:col-span-2">
            <Field label="Internal note" hint="Staff only — never shown to the customer">
              <Textarea
                className="min-h-[70px] rounded-none"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="repair-note-input"
              />
            </Field>
          </div>
        </div>
      </section>

      <section className="mt-5 border border-border p-5">
        <h2 className="text-base font-bold md:text-lg">Photos of the piece</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Take a picture of the item as it arrives. Photos are shrunk automatically so they stay
          small.
        </p>
        <div className="mt-4">
          <PhotoCapture items={attachments} onChange={setAttachments} testid="repair-photos" />
        </div>
      </section>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button
          className="rounded-none bg-[#C5A059] px-6 font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
          onClick={save}
          disabled={saving}
          data-testid="save-repair-button"
        >
          <Save className="mr-1.5 h-4 w-4" /> {saving ? "Booking in…" : "Book in the repair"}
        </Button>
        <Button
          variant="outline"
          className="rounded-none"
          onClick={() => navigate("/repairs")}
          data-testid="cancel-repair-button"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
